import React from 'react';
import './EtsyUploadPopup.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function EtsyUploadPopup({
  showEtsyUploadPopup,
  setShowEtsyUploadPopup,
  etsyFileInputRef,
  selectedEtsyFile,
  setSelectedEtsyFile,
  parsedEtsyData,
  setParsedEtsyData,
  uploadEtsyStatus,
  setUploadEtsyStatus,
  handleEtsyFileUpload,
  handleEtsyUploadSubmit,
  uploadEtsyWeek,
  setUploadEtsyWeek,
  uploadEtsyMonth,
  setUploadEtsyMonth,
  uploadEtsyYear,
  setUploadEtsyYear,
  etsyPeriodMode,
  setEtsyPeriodMode
}) {
  const isWeekly = etsyPeriodMode === 'weekly';

  const closeEtsyUploadPopup = () => {
    setShowEtsyUploadPopup(false);
    setSelectedEtsyFile(null);
    setParsedEtsyData([]);
    setUploadEtsyStatus(null);
    setUploadEtsyWeek('');
    setUploadEtsyMonth('');
    setUploadEtsyYear('');
    if (etsyFileInputRef.current) etsyFileInputRef.current.value = '';
  };

  const handlePeriodModeChange = (mode) => {
    setEtsyPeriodMode(mode);
    setParsedEtsyData([]);
    setUploadEtsyStatus(null);
    setUploadEtsyWeek('');
    setUploadEtsyMonth('');
  };

  const isSubmitDisabled =
    !selectedEtsyFile ||
    !uploadEtsyYear ||
    (isWeekly ? !uploadEtsyWeek : !uploadEtsyMonth) ||
    parsedEtsyData.length === 0 ||
    uploadEtsyStatus?.type === 'loading';

  return (
    <>
      {showEtsyUploadPopup && (
        <div className="etsy-popup-overlay">
          <div className="etsy-popup-content">
            <div className="etsy-popup-header">
              <h2>Upload Etsy Sales Data</h2>
              <button className="etsy-close-popup" onClick={closeEtsyUploadPopup}>✕</button>
            </div>

            <div className="etsy-upload-form">

              {/* ── Period mode toggle ── */}
              <div className="etsy-form-group">
                <label>Period Type</label>
                <div className="etsy-mode-buttons">
                  <button
                    className={`etsy-mode-btn ${isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('weekly')}
                  >
                    Weekly
                  </button>
                  <button
                    className={`etsy-mode-btn ${!isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('monthly')}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* ── File format info ── */}
              <div className="etsy-file-info">
                <h3>File Format Requirements</h3>
                <ul>
                  <li><strong>Required columns:</strong> Item ID, QTY</li>
                  <li><strong>Note:</strong> System will sum quantities by Item ID if multiple rows exist</li>
                  <li><strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)</li>
                  <li><strong>Auto-detection:</strong> System will automatically find header row</li>
                  <li><strong>File size:</strong> Maximum 10MB</li>
                </ul>
              </div>

              {/* ── Period fields ── */}
              <div className="form-row">
                {isWeekly ? (
                  <div className="etsy-form-group half-width">
                    <label>Week Number *</label>
                    <input
                      type="number" min="1" max="53"
                      value={uploadEtsyWeek}
                      onChange={(e) => setUploadEtsyWeek(e.target.value)}
                      placeholder="Week (1-53)"
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                ) : (
                  <div className="etsy-form-group half-width">
                    <label>Month *</label>
                    <select
                      value={uploadEtsyMonth}
                      onChange={(e) => setUploadEtsyMonth(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    >
                      <option value="">Select month</option>
                      {MONTHS.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="etsy-form-group half-width">
                  <label>Year *</label>
                  <input
                    type="number" min="2020" max="2030"
                    value={uploadEtsyYear}
                    onChange={(e) => setUploadEtsyYear(e.target.value)}
                    placeholder="Year"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* ── File picker ── */}
              <div className="etsy-form-group">
                <label>Select File *</label>
                <div className="etsy-file-upload">
                  <input
                    type="file" ref={etsyFileInputRef}
                    accept=".csv,.xlsx,.xls"
                    onChange={handleEtsyFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="etsy-file-upload-btn"
                    onClick={() => etsyFileInputRef.current.click()}
                  >
                    {selectedEtsyFile ? selectedEtsyFile.name : 'Choose CSV or Excel file'}
                  </button>
                  {selectedEtsyFile && (
                    <span className="etsy-file-size">
                      {(selectedEtsyFile.size / 1024).toFixed(2)} KB
                    </span>
                  )}
                </div>
                <p className="etsy-file-hint">The system will automatically detect the header row with column names</p>
              </div>

              {/* ── Status ── */}
              {uploadEtsyStatus && (
                <div className={`etsy-upload-status ${uploadEtsyStatus.type}`}>
                  {uploadEtsyStatus.type === 'loading' ? `⏳ ${uploadEtsyStatus.message}` : uploadEtsyStatus.message}
                  {uploadEtsyStatus.details && (
                    <div className="etsy-status-details">{uploadEtsyStatus.details}</div>
                  )}
                </div>
              )}

              {/* ── Preview table ── */}
              {parsedEtsyData.length > 0 && (
                <div className="etsy-data-preview">
                  <div className="etsy-preview-header">
                    <h3>Data Preview ({parsedEtsyData.length} aggregated rows)</h3>
                    <div className="etsy-preview-stats">
                      <span className="etsy-stat-item">
                        Unique Item IDs: {new Set(parsedEtsyData.map(r => r.itemId)).size}
                      </span>
                      <span className="etsy-stat-item">
                        Total Quantity: {parsedEtsyData.reduce((s, r) => s + (r.quantity || 0), 0).toLocaleString()}
                      </span>
                      <span className="etsy-stat-item">
                        {isWeekly ? `Week: ${uploadEtsyWeek}` : `Month: ${MONTHS[uploadEtsyMonth - 1] || uploadEtsyMonth}`}, Year: {uploadEtsyYear}
                      </span>
                    </div>
                  </div>
                  <div className="etsy-preview-table-container">
                    <table className="etsy-preview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Item ID (SKU)</th>
                          <th>Aggregated Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedEtsyData.slice(0, 15).map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className="etsy-itemid-cell">{row.itemId || 'N/A'}</td>
                            <td className="etsy-number-cell">
                              <span className={row.quantity > 0 ? 'etsy-positive-value' : 'etsy-zero-value'}>
                                {row.quantity || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedEtsyData.length > 15 && (
                      <p className="etsy-preview-more">⋮ ... and {parsedEtsyData.length - 15} more aggregated Item IDs</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Buttons ── */}
              <div className="etsy-popup-buttons">
                <button className="etsy-cancel-btn" onClick={closeEtsyUploadPopup}>Cancel</button>
                <button
                  className="etsy-submit-btn"
                  onClick={handleEtsyUploadSubmit}
                  disabled={isSubmitDisabled}
                >
                  {uploadEtsyStatus?.type === 'loading' ? 'Processing...' : 'Upload to Database'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EtsyUploadPopup;